// src/pages/qc/PageQCCoa.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { downloadPdf } from "../../apiService";
import { CoaCreateModal } from "./CoaCreateModal";
import { fmtDate, currentMonthKey } from "../../utils/dateUtils";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { useToast } from "../../components/Toast";

const TYPE_LABELS = { WB: "Water Based", BF: "Brake Fluid", COOLANT: "Coolant", HM: "Hot Melt" };
const TYPE_COLORS = {
  WB:      { bg: "#dbeafe", color: "#1e40af" },
  BF:      { bg: "#fee2e2", color: "#991b1b" },
  COOLANT: { bg: "#d1fae5", color: "#065f46" },
  HM:      { bg: "#fef3c7", color: "#92400e" },
};

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

function formatDate(isoStr) {
  return fmtDate(isoStr);
}

export function PageQCCoa({ myPerms, fgs, sfgs }) {
  const showToast = useToast();
  const [coas,    setCoas]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,     setSearch]     = useState("");
  const [monthKey,   setMonthKey]   = useState(currentMonthKey);
  const [modal,      setModal]      = useState(false);
  const [typeFilter, setTypeFilter] = useState("ALL");

  const canEdit   = myPerms?.includes("QC_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    api.fetchCoas()
      .then(setCoas)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return coas.filter(c =>
      (c.createdAt || "").startsWith(monthKey) &&
      (typeFilter === "ALL" || c.productType === typeFilter) &&
      (c.coaNumber.toLowerCase().includes(q) ||
       c.productId.toLowerCase().includes(q) ||
       (c.customerName || "").toLowerCase().includes(q) ||
       (c.invoiceNo || "").toLowerCase().includes(q))
    );
  }, [coas, search, typeFilter, monthKey]);

  async function handleCreate(body) {
    const created = await api.createCoa(body);
    setCoas(old => [created, ...old]);
    showToast("COA created");
    setModal(false);
  }

  async function handleDelete(coa) {
    if (!window.confirm(`Delete COA ${coa.coaNumber}?`)) return;
    await api.deleteCoa(coa.id);
    setCoas(old => old.filter(c => c.id !== coa.id));
    showToast("COA deleted", "info");
  }

  function handleDownloadPdf(coa) {
    downloadPdf(`/api/qc/coa/${coa.id}/pdf`, coa.coaNumber + ".pdf");
  }

  const colCount = 7 + (canDelete ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Certificate of Analysis</div>
          <div className="page-subtitle">Generate and download COAs for all product types</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setModal(true)}>+ New COA</button>
          </div>
        )}
      </div>

      <div className="action-bar" style={{ gap: 10, flexWrap: "wrap" }}>
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search COA no, product, customer…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
        <select className="form-input" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 150 }}>
          <option value="ALL">All Types</option>
          <option value="WB">Water Based</option>
          <option value="BF">Brake Fluid</option>
          <option value="COOLANT">Coolant</option>
          <option value="HM">Hot Melt</option>
        </select>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>COA Number</th>
                <th>Type</th>
                <th>Product</th>
                <th>Customer</th>
                <th>Invoice No</th>
                <th>Date Issued</th>
                <th>PDF</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(coa => (
                <tr key={coa.id}>
                  <td className="fw-600" style={{ fontFamily: "monospace" }}>{coa.coaNumber}</td>
                  <td><TypeBadge type={coa.productType} /></td>
                  <td>{coa.productId}</td>
                  <td>{coa.customerName || "—"}</td>
                  <td>{coa.invoiceNo || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDate(coa.createdAt)}</td>
                  <td>
                    <button className="btn-small" onClick={() => handleDownloadPdf(coa)}
                      title="Download PDF" style={{ padding: "3px 10px" }}>
                      ⬇ PDF
                    </button>
                  </td>
                  {canDelete && (
                    <td>
                      <button className="btn-small btn-danger" onClick={() => handleDelete(coa)}>Del</button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                    No COA records found. {canEdit && "Click '+ New COA' to create one."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <CoaCreateModal
            fgs={fgs}
            sfgs={sfgs}
            onSave={handleCreate}
            onCancel={() => setModal(false)}
          />
        </div>
      )}
    </>
  );
}
